(() => {
  const body = document.body;
  let lenisInstance = null;
  let smoothScrollInitialized = false;
  let smoothScrollAttempts = 0;

  const onReady = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  };

  const setScrollLock = (locked) => {
    body.classList.toggle("gf-lock", locked);
    if (!lenisInstance) return;
    if (locked && lenisInstance.stop) lenisInstance.stop();
    if (!locked && lenisInstance.start) lenisInstance.start();
  };

  const initSmoothScroll = () => {
    if (smoothScrollInitialized) return;
    const touchDevice =
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(pointer: coarse)").matches ||
      "ontouchstart" in window;

    if (touchDevice) {
      smoothScrollInitialized = true;
      return;
    }

    if (typeof Lenis === "undefined") {
      smoothScrollAttempts += 1;
      if (smoothScrollAttempts < 20) {
        window.setTimeout(initSmoothScroll, 150);
      }
      return;
    }

    smoothScrollInitialized = true;
    lenisInstance = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      orientation: "vertical",
    });

    const raf = (time) => {
      lenisInstance.raf(time);
      requestAnimationFrame(raf);
    };

    requestAnimationFrame(raf);
  };

  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

  const waitForWindowLoad = () =>
    new Promise((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        window.addEventListener("load", resolve, { once: true });
      }
    });

  const initPreloader = () => {
    const loader = document.querySelector("[data-gf-loader]");
    if (!loader) {
      initSmoothScroll();
      return;
    }

    Promise.all([waitForWindowLoad(), wait(900)])
      .then(() => {
        loader.classList.add("is-ready");
        // long enough for the staggered final fill (315ms delay + 720ms ease)
        return wait(1100);
      })
      .then(() => {
        loader.classList.add("is-complete");
        body.classList.remove("gf-loading");
        document.dispatchEvent(new CustomEvent("gf:loader:complete"));
        initSmoothScroll();
        window.setTimeout(() => loader.remove(), 640);
      });
  };

  const initSmoothAnchorLinks = () => {
    const getTarget = (link) => {
      const href = link.getAttribute("href");
      if (!href || !href.includes("#")) return null;

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.pathname.replace(/\/$/, "") !== window.location.pathname.replace(/\/$/, "")) return null;
      if (!url.hash || url.hash === "#") return null;

      return document.getElementById(decodeURIComponent(url.hash.slice(1)));
    };

    const scrollToTarget = (target, hash) => {
      const header = document.querySelector(".gf-header");
      const offset = header ? header.getBoundingClientRect().height + 16 : 0;

      if (lenisInstance && lenisInstance.scrollTo) {
        lenisInstance.scrollTo(target, { offset: -offset });
      } else {
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }

      history.pushState(null, "", hash);

      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      setTimeout(() => target.focus({ preventScroll: true }), 450);
    };

    document.querySelectorAll('a[href*="#"]').forEach((link) => {
      const target = getTarget(link);
      if (!target) return;

      link.addEventListener("click", (event) => {
        const nextTarget = getTarget(link);
        if (!nextTarget) return;

        event.preventDefault();
        const hash = new URL(link.getAttribute("href"), window.location.origin).hash;
        scrollToTarget(nextTarget, hash);
      });
    });
  };

  onReady(initPreloader);
  onReady(initSmoothAnchorLinks);

  const loadVideo = (video) => {
    if (!video || video.dataset.gfVideoLoaded === "true") return;
    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });
    video.dataset.gfVideoLoaded = "true";
    video.load();
    video.play().catch(() => {});
  };

  const initLazyVideos = () => {
    const videos = [...document.querySelectorAll("[data-gf-lazy-video]")];
    if (!videos.length) return;

    if (!("IntersectionObserver" in window)) {
      videos.forEach(loadVideo);
      return;
    }

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadVideo(entry.target);
          videoObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "300px 0px" }
    );

    videos.forEach((video) => videoObserver.observe(video));
  };

  onReady(initLazyVideos);

  const newsletterDismissedKey = "grainify-newsletter-dismissed";
  const isNewsletterDismissed = () => {
    try {
      return window.localStorage.getItem(newsletterDismissedKey) === "true";
    } catch (error) {
      return false;
    }
  };

  const dismissNewsletter = () => {
    try {
      window.localStorage.setItem(newsletterDismissedKey, "true");
    } catch (error) {
      return;
    }
  };

  document.querySelectorAll("[data-gf-toggle]").forEach((button) => {
    button.addEventListener("click", () => {
      const target = document.querySelector(button.getAttribute("data-gf-toggle"));
      if (!target) return;
      const open = target.classList.toggle("is-open");
      button.setAttribute("aria-expanded", String(open));
      target.setAttribute("aria-hidden", String(!open));
      if (target.classList.contains("gf-mobile-panel")) {
        setScrollLock(open);
      }
    });
  });

  document.querySelectorAll("[data-gf-close]").forEach((button) => {
    button.addEventListener("click", () => {
      const target = document.querySelector(button.getAttribute("data-gf-close"));
      if (!target) return;
      if (target.id === "grainify-newsletter-modal") dismissNewsletter();
      target.classList.remove("is-open");
      target.setAttribute("aria-hidden", "true");
      setScrollLock(false);
    });
  });

  document.querySelectorAll("[data-gf-drawer-open]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      const drawer = document.querySelector(button.getAttribute("data-gf-drawer-open"));
      if (!drawer) return;
      document.querySelectorAll(".gf-mobile-panel.is-open").forEach((panel) => {
        panel.classList.remove("is-open");
        panel.setAttribute("aria-hidden", "true");
      });
      document.querySelectorAll("[data-gf-toggle][aria-expanded='true']").forEach((toggle) => toggle.setAttribute("aria-expanded", "false"));
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      setScrollLock(true);
    });
  });

  const openModal = (modal) => {
    if (!modal || modal.classList.contains("is-open")) return;
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    setScrollLock(true);
    const input = modal.querySelector("input[type='email']") || modal.querySelector("input") || modal.querySelector("button");
    if (input) window.setTimeout(() => input.focus(), 120);
  };

  document.querySelectorAll("[data-gf-modal-open]").forEach((button) => {
    button.addEventListener("click", () => {
      const modal = document.querySelector(button.getAttribute("data-gf-modal-open"));
      openModal(modal);
    });
  });

  const initNewsletterTrigger = () => {
    const modal = document.querySelector("#grainify-newsletter-modal");
    if (!modal) return;
    if (modal.dataset.autoOpen !== "true") return;
    if (isNewsletterDismissed()) return;
    let shown = false;
    let fallbackTimer;

    const getScrollProgress = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return 0;
      return window.scrollY / scrollable;
    };

    const startsPastScrollTrigger = getScrollProgress() >= 0.35;

    const showNewsletter = () => {
      if (shown) return;
      if (isNewsletterDismissed()) return;
      shown = true;
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("scroll", handleScroll);
      openModal(modal);
    };

    function handleScroll() {
      if (getScrollProgress() >= 0.35) showNewsletter();
    }

    fallbackTimer = window.setTimeout(showNewsletter, 30000);
    if (!startsPastScrollTrigger) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }
  };

  if (body.classList.contains("gf-loading")) {
    document.addEventListener("gf:loader:complete", initNewsletterTrigger, { once: true });
  } else {
    initNewsletterTrigger();
  }

  // Gallery: the images sit in a horizontal "train"; picking a thumbnail
  // slides the track so that car comes into view.
  document.querySelectorAll("[data-gf-gallery]").forEach((gallery) => {
    const track = gallery.querySelector("[data-gf-gallery-track]");
    const stage = gallery.querySelector("[data-gf-gallery-stage]");
    const thumbs = [...gallery.querySelectorAll("[data-gf-thumb]")];
    if (!track || thumbs.length === 0) return;

    const select = (index) => {
      track.style.transform = `translateX(-${index * 100}%)`;
      thumbs.forEach((thumb, i) => thumb.classList.toggle("is-active", i === index));
      if (stage) {
        stage.style.setProperty("--gf-zoom-x", "50%");
        stage.style.setProperty("--gf-zoom-y", "50%");
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const index = parseInt(thumb.getAttribute("data-gf-index") || "0", 10);
        select(index);
      });
    });
  });

  document.querySelectorAll("[data-gf-gallery-stage]").forEach((stage) => {
    stage.addEventListener("pointermove", (event) => {
      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
      const bounds = stage.getBoundingClientRect();
      const x = ((event.clientX - bounds.left) / bounds.width) * 100;
      const y = ((event.clientY - bounds.top) / bounds.height) * 100;
      stage.style.setProperty("--gf-zoom-x", `${Math.max(0, Math.min(100, x))}%`);
      stage.style.setProperty("--gf-zoom-y", `${Math.max(0, Math.min(100, y))}%`);
    });

    stage.addEventListener("pointerleave", () => {
      stage.style.setProperty("--gf-zoom-x", "50%");
      stage.style.setProperty("--gf-zoom-y", "50%");
    });
  });

  document.querySelectorAll("[data-gf-qty]").forEach((control) => {
    const input = control.querySelector("input");
    control.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", () => {
        if (!input) return;
        const current = Math.max(1, parseInt(input.value || "1", 10));
        const next = button.dataset.gfQtyAction === "minus" ? Math.max(1, current - 1) : current + 1;
        input.value = String(next);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  });

  document.querySelectorAll("[data-gf-product-form]").forEach((form) => {
    const qtyInput = form.querySelector("input[name='quantity']");
    const submit = form.querySelector("[data-gf-product-submit]");
    const variantSelect = form.querySelector("[data-gf-variant-select]");
    const singleVariant = form.querySelector("input[name='id'][data-gf-variant-price]");
    const optionInputs = [...form.querySelectorAll("[data-gf-option-input]")];
    const sellingPlanInput = form.querySelector("[data-gf-selling-plan-input]");
    const frequencySelect = form.querySelector("[data-gf-frequency]");

    // Displays outside the form that mirror the selected offer's price
    const pdp = form.closest("[data-gf-pdp]");
    const priceCurrent = pdp && pdp.querySelector("[data-gf-price-current]");
    const priceWas = pdp && pdp.querySelector("[data-gf-price-was]");
    const priceFlag = pdp && pdp.querySelector("[data-gf-price-flag]");
    const stickyPrice = document.querySelector("[data-gf-sticky-price]");
    const stickyWas = document.querySelector("[data-gf-sticky-was]");

    const formatMoney = (cents, fallback) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") return window.Shopify.formatMoney(cents);
      const amount = (cents / 100).toFixed(2);
      if (!fallback) return amount;
      return fallback.replace(/\d[\d,]*(?:\.\d+)?/, amount);
    };

    const getVariantData = () => {
      if (variantSelect) {
        const option = variantSelect.selectedOptions[0];
        return {
          price: parseInt(option.dataset.gfVariantPrice || "0", 10),
          money: option.dataset.gfVariantMoney || "",
          available: option.dataset.gfVariantAvailable === "true",
        };
      }

      if (singleVariant) {
        return {
          price: parseInt(singleVariant.dataset.gfVariantPrice || "0", 10),
          money: singleVariant.dataset.gfVariantMoney || "",
          available: singleVariant.dataset.gfVariantAvailable === "true",
        };
      }

      return { price: 0, money: "", available: true };
    };

    // The selected offer discounts the variant price (Subscribe & Save)
    const getOffer = (variant) => {
      const selected = optionInputs.find((input) => input.checked);
      const pct = selected ? parseInt(selected.dataset.gfDiscountPct || "0", 10) : 0;
      return {
        subscribe: selected ? selected.value === "subscribe" : false,
        pct,
        price: Math.round((variant.price * (100 - pct)) / 100),
      };
    };

    const updatePricing = () => {
      const variant = getVariantData();
      const offer = getOffer(variant);
      const discounted = offer.pct > 0;

      if (submit) {
        const qty = Math.max(1, parseInt((qtyInput && qtyInput.value) || "1", 10));
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? `Add to Cart — ${formatMoney(offer.price * qty, variant.money)}` : "Sold Out";
      }

      // Without purchase options the price display is server-rendered and
      // static (may show a compare-at strike) — leave it alone.
      if (optionInputs.length > 0) {
        if (priceCurrent) priceCurrent.textContent = formatMoney(offer.price, variant.money);
        if (priceWas) {
          priceWas.textContent = formatMoney(variant.price, variant.money);
          priceWas.hidden = !discounted;
        }
        if (priceFlag) priceFlag.hidden = !discounted;
        if (stickyPrice) stickyPrice.textContent = formatMoney(offer.price, variant.money);
        if (stickyWas) {
          stickyWas.textContent = formatMoney(variant.price, variant.money);
          stickyWas.hidden = !discounted;
        }
      }

      // Real selling plan (when the store has one) rides along on submit
      if (sellingPlanInput) {
        sellingPlanInput.value = offer.subscribe && frequencySelect ? frequencySelect.value : "";
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        form.querySelectorAll("[data-gf-option]").forEach((option) => {
          option.classList.toggle("is-selected", option.contains(input) && input.checked);
        });
        updatePricing();
      });
    });

    updatePricing();
    if (qtyInput) qtyInput.addEventListener("change", updatePricing);
    if (variantSelect) variantSelect.addEventListener("change", updatePricing);
    if (frequencySelect) frequencySelect.addEventListener("change", updatePricing);
  });

  // Custom dropdown (delivery frequency): a hidden input carries the value
  // for the form; the button/list pair is fully styleable packaging UI.
  document.querySelectorAll("[data-gf-dropdown]").forEach((dropdown) => {
    const toggle = dropdown.querySelector("[data-gf-dropdown-toggle]");
    const list = dropdown.querySelector("[data-gf-dropdown-list]");
    const label = dropdown.querySelector("[data-gf-dropdown-label]");
    const value = dropdown.querySelector("[data-gf-frequency]");
    const options = [...dropdown.querySelectorAll("[data-gf-dropdown-option]")];
    if (!toggle || !list || options.length === 0) return;

    const selectedOption = () => options.find((option) => option.getAttribute("aria-selected") === "true") || options[0];

    const setOpen = (open) => {
      dropdown.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", String(open));
      list.hidden = !open;
      if (open) selectedOption().focus();
    };

    const select = (option) => {
      options.forEach((other) => other.setAttribute("aria-selected", String(other === option)));
      if (label) label.textContent = option.textContent.trim();
      if (value) {
        value.value = option.dataset.gfValue || "";
        value.dispatchEvent(new Event("change", { bubbles: true }));
      }
      setOpen(false);
      toggle.focus();
    };

    toggle.addEventListener("click", () => setOpen(list.hidden));
    toggle.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setOpen(true);
      }
    });

    options.forEach((option, index) => {
      option.addEventListener("click", () => select(option));
      option.addEventListener("keydown", (event) => {
        let target = null;
        if (event.key === "ArrowDown") target = options[Math.min(options.length - 1, index + 1)];
        if (event.key === "ArrowUp") target = options[Math.max(0, index - 1)];
        if (event.key === "Home") target = options[0];
        if (event.key === "End") target = options[options.length - 1];
        if (target) {
          event.preventDefault();
          target.focus();
        }
      });
    });

    dropdown.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !list.hidden) {
        setOpen(false);
        toggle.focus();
      }
    });

    document.addEventListener("click", (event) => {
      if (!list.hidden && !dropdown.contains(event.target)) setOpen(false);
    });
  });

  // Sticky add-to-cart: slides in once the buy box has scrolled away.
  const initStickyAtc = () => {
    const sticky = document.querySelector("[data-gf-sticky-atc]");
    const form = document.querySelector("[data-gf-product-form]");
    if (!sticky || !form) return;

    const addButton = sticky.querySelector("[data-gf-sticky-add]");
    if (addButton) {
      addButton.addEventListener("click", () => {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }

    // Scroll-driven (not IntersectionObserver): instant jumps can skip past
    // the form without ever intersecting, which would leave stale state.
    let stickyFrame = null;
    const syncSticky = () => {
      stickyFrame = null;
      const passed = form.getBoundingClientRect().bottom < 0;
      sticky.classList.toggle("is-visible", passed);
      sticky.setAttribute("aria-hidden", String(!passed));
      // inert removes the hidden pill's button from the tab order
      sticky.toggleAttribute("inert", !passed);
    };
    const requestStickySync = () => {
      if (stickyFrame === null) stickyFrame = window.requestAnimationFrame(syncSticky);
    };

    syncSticky();
    window.addEventListener("scroll", requestStickySync, { passive: true });
    window.addEventListener("resize", requestStickySync);
  };

  onReady(initStickyAtc);

  const revealObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add("is-visible");
              revealObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.18, rootMargin: "0px 0px -6% 0px" }
        )
      : null;

  const observeReveals = (root) => {
    (root || document).querySelectorAll("[data-gf-reveal]:not(.is-visible)").forEach((element) => {
      if (revealObserver) {
        revealObserver.observe(element);
      } else {
        element.classList.add("is-visible");
      }
    });
  };

  observeReveals();

  // Sections are re-rendered in place by the theme editor and dev hot reload
  document.addEventListener("shopify:section:load", (event) => observeReveals(event.target));

  document.querySelectorAll("[data-gf-view-toggle]").forEach((toggle) => {
    const section = document.querySelector("[data-gf-collection-view]");
    if (!section) return;

    toggle.querySelectorAll("[data-gf-view]").forEach((button) => {
      button.addEventListener("click", () => {
        const list = button.dataset.gfView === "list";
        section.classList.toggle("is-list-view", list);
        toggle.querySelectorAll("[data-gf-view]").forEach((item) => {
          const active = item === button;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-pressed", String(active));
        });
      });
    });
  });

  // Horizontal rails — arrow controls scroll one "page" and reflect bounds.
  const initRails = (root) => {
    (root || document).querySelectorAll("[data-gf-rail]").forEach((rail) => {
      if (rail.dataset.gfRailReady === "true") return;
      rail.dataset.gfRailReady = "true";
      const viewport = rail.querySelector("[data-gf-rail-viewport]");
      const prev = rail.querySelector("[data-gf-rail-prev]");
      const next = rail.querySelector("[data-gf-rail-next]");
      if (!viewport) return;

      const step = () => {
        const first = viewport.firstElementChild;
        const gap = parseFloat(getComputedStyle(viewport).columnGap || "0") || 0;
        const card = first ? first.getBoundingClientRect().width + gap : viewport.clientWidth * 0.8;
        return Math.max(card, viewport.clientWidth * 0.6);
      };

      const update = () => {
        const max = viewport.scrollWidth - viewport.clientWidth - 1;
        const atStart = viewport.scrollLeft <= 1;
        const atEnd = viewport.scrollLeft >= max;
        const overflowing = viewport.scrollWidth > viewport.clientWidth + 2;
        if (prev) prev.disabled = atStart || !overflowing;
        if (next) next.disabled = atEnd || !overflowing;
        rail.classList.toggle("is-static", !overflowing);
      };

      if (prev) prev.addEventListener("click", () => viewport.scrollBy({ left: -step(), behavior: "smooth" }));
      if (next) next.addEventListener("click", () => viewport.scrollBy({ left: step(), behavior: "smooth" }));
      viewport.addEventListener("scroll", () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener("resize", update);
      update();
    });
  };

  onReady(() => initRails());
  document.addEventListener("shopify:section:load", (event) => initRails(event.target));

  // Header: keep --gf-header-h exact per breakpoint, and solidify the
  // transparent homepage header once the page scrolls.
  const initHeader = () => {
    const header = document.querySelector(".gf-header");
    if (!header) return;

    const syncHeight = () => {
      const h = header.getBoundingClientRect().height;
      if (h) document.documentElement.style.setProperty("--gf-header-h", `${h}px`);
    };
    const syncScrolled = () => {
      header.classList.toggle("is-scrolled", window.scrollY > 16);
    };

    syncHeight();
    syncScrolled();
    window.addEventListener("resize", syncHeight);
    window.addEventListener("scroll", syncScrolled, { passive: true });
  };

  onReady(() => initHeader());

  // Single-open accordions — opening one panel closes its siblings.
  document.querySelectorAll('[data-gf-accordion="single"]').forEach((group) => {
    const panels = [...group.querySelectorAll(":scope > details")];
    panels.forEach((panel) => {
      panel.addEventListener("toggle", () => {
        if (!panel.open) return;
        panels.forEach((other) => {
          if (other !== panel) other.open = false;
        });
      });
    });
  });

  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll(".gf-cart-drawer.is-open, .gf-mobile-panel.is-open, .gf-modal.is-open").forEach((el) => {
      if (el.id === "grainify-newsletter-modal") dismissNewsletter();
      el.classList.remove("is-open");
      el.setAttribute("aria-hidden", "true");
    });
    document.querySelectorAll("[data-gf-toggle][aria-expanded='true']").forEach((button) => button.setAttribute("aria-expanded", "false"));
    setScrollLock(false);
  });
})();
